// React related imports
import React, { createContext, useContext, useEffect, useState } from 'react';

// Auth Context
import { useAuth } from './AuthProvider';

// Interfaces
import {
  UserInfo,
  UserInfoResponse,
} from '../common/interfaces/UserInfo.interface';

const UserInfoContext = createContext<{
  userInfo: UserInfo | null;
  loadingUserInfo: boolean;
}>({
  userInfo: null,
  loadingUserInfo: true,
});

export const UserInfoProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated } = useAuth();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setUserInfo(null);
      setLoadingUserInfo(false);
      return;
    }

    // Set loading to true when we start fetching
    setLoadingUserInfo(true);

    fetch(`${process.env.REACT_APP_BACKEND_URL}/dashboard-auth/userInfo`, {
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : Promise.reject('Failed')))
      .then((resBody: UserInfoResponse) => {
        setUserInfo(resBody.data);
      })
      .catch(() => setUserInfo(null))
      .finally(() => setLoadingUserInfo(false));
  }, [isAuthenticated]);

  return (
    <UserInfoContext.Provider value={{ userInfo, loadingUserInfo }}>
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = () => useContext(UserInfoContext);
